import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import {
  Shield, LayoutDashboard, ClipboardList, FileCheck2, BarChart3, ScrollText,
  BookOpen, Building2, Users, ShieldCheck, Sparkles, LogOut, ChevronDown,
  Award, TrendingUp, Map, Landmark, Compass, Layers, FileText, GraduationCap,
  ClipboardCheck
} from "lucide-react";

const NAV = [
  { section: "Overview", items: [
    { to: "/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/board", label: "Board Summary", icon: TrendingUp, testid: "nav-board" },
  ]},
  { section: "Assessment", items: [
    { to: "/requirements", label: "Requirement Assessment", icon: ClipboardList, testid: "nav-requirements" },
    { to: "/evidence", label: "Evidence Register", icon: FileCheck2, testid: "nav-evidence" },
    { to: "/scoring", label: "Scoring Guide", icon: Compass, testid: "nav-scoring" },
  ]},
  { section: "Reports", items: [
    { to: "/report", label: "Enterprise Report", icon: FileText, testid: "nav-report" },
    { to: "/framework-report", label: "Framework Report", icon: ClipboardCheck, testid: "nav-framework-report" },
    { to: "/management", label: "Management Summary", icon: BarChart3, testid: "nav-management" },
    { to: "/certification", label: "Certification Readiness", icon: Award, testid: "nav-certification" },
    { to: "/risk-register", label: "Risk Register", icon: ScrollText, testid: "nav-risk" },
    { to: "/improvement", label: "Improvement Plan", icon: Layers, testid: "nav-improvement" },
    { to: "/roadmap", label: "90-Day Roadmap", icon: Map, testid: "nav-roadmap" },
  ]},
  { section: "Intelligence", items: [
    { to: "/ai", label: "AI Recommendations", icon: Sparkles, testid: "nav-ai" },
    { to: "/knowledge", label: "Knowledge Center", icon: BookOpen, testid: "nav-knowledge" },
    { to: "/glossary", label: "Glossary", icon: GraduationCap, testid: "nav-glossary" },
  ]},
  { section: "Administration", items: [
    { to: "/organization", label: "Organization Settings", icon: Building2, testid: "nav-org", roles: ["admin","superadmin"] },
    { to: "/users", label: "User Management", icon: Users, testid: "nav-users", roles: ["admin","superadmin"] },
    { to: "/tenants", label: "Tenant Management", icon: Landmark, testid: "nav-tenants", roles: ["superadmin"] },
    { to: "/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-audit" },
  ]},
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { tenants, activeTenantId, setActiveTenantId, currentTenant } = useTenant();
  const navigate = useNavigate();
  const [showTenantMenu, setShowTenantMenu] = useState(false);

  const canSee = (roles) => !roles || roles.includes(user?.role);
  const roleColor = { superadmin: "text-[#F59E0B]", admin: "text-[#00E5FF]", auditor: "text-[#60A5FA]", viewer: "text-slate-400" }[user?.role];

  return (
    <div className="flex min-h-screen bg-[#050914]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-800/70 flex flex-col shrink-0 bg-[#050914] sticky top-0 h-screen">
        <div className="p-4 border-b border-slate-800/70">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center">
              <Shield size={16} className="text-[#00E5FF]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading text-sm font-black tracking-tight">GACRF<span className="text-[#00E5FF]">®</span></div>
              <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500">Resilience Platform</div>
            </div>
          </div>
        </div>

        {/* Tenant switcher */}
        {user?.role === "superadmin" && (
          <div className="relative p-3 border-b border-slate-800/70">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5">Active Tenant</div>
            <button
              data-testid="tenant-switcher"
              onClick={() => setShowTenantMenu((v) => !v)}
              className="w-full text-left px-3 py-2 bg-[#0F172A] border border-slate-800 hover:border-slate-600 transition-colors flex items-center justify-between"
            >
              <div>
                <div className="text-xs font-semibold text-slate-100">{currentTenant?.name || "Select tenant"}</div>
                <div className="text-[10px] font-mono text-slate-500">{currentTenant?.slug || "—"}</div>
              </div>
              <ChevronDown size={14} className={`text-slate-500 transition-transform ${showTenantMenu ? "rotate-180" : ""}`} />
            </button>
            {showTenantMenu && (
              <div className="absolute left-3 right-3 mt-1 bg-[#0F172A] border border-slate-700 z-30 max-h-64 overflow-y-auto">
                {tenants.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`tenant-option-${t.slug}`}
                    onClick={() => { setActiveTenantId(t.id); setShowTenantMenu(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-800/50 border-b border-slate-800/50 ${t.id === activeTenantId ? "bg-[#00E5FF]/5 border-l-2 border-l-[#00E5FF]" : ""}`}
                  >
                    <div className="text-xs font-medium text-slate-100">{t.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{t.slug} · {t.sector}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {user?.role !== "superadmin" && currentTenant && (
          <div className="p-3 border-b border-slate-800/70">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">Tenant</div>
            <div className="flex items-center gap-2">
              {currentTenant.logo_data_url && (
                <img src={currentTenant.logo_data_url} alt={currentTenant.name} style={{ height: 20, maxWidth: 40 }} />
              )}
              <div>
                <div className="text-xs font-semibold text-slate-100" data-testid="current-tenant-name">{currentTenant.name}</div>
                <div className="text-[10px] font-mono text-slate-500">{currentTenant.sector}</div>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map((sec) => (
            <div key={sec.section}>
              <div className="nav-section">{sec.section}</div>
              {sec.items.filter((it) => canSee(it.roles)).map((it) => (
                <NavLink key={it.to} to={it.to} className={({isActive}) => `nav-item ${isActive ? "active" : ""}`} data-testid={it.testid}>
                  <it.icon size={14} strokeWidth={1.75} />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-slate-800/70">
          <div className="mb-2">
            <div className="text-xs font-semibold text-slate-100 truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[10px] font-mono text-slate-500 truncate">{user?.email}</div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.15em] mt-1 ${roleColor}`} data-testid="user-role">
              {user?.role}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="btn btn-secondary w-full justify-center btn-sm"
          >
            <LogOut size={12} strokeWidth={2} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700/40 to-transparent" />
        {children}
      </main>
    </div>
  );
}
